import { PaginationFilterRequest } from './core.model';
import { utcToJSDate } from '@/utils/date-time';

export enum PointAccountStatus {
  Draft = 1,
  Active = 2,
  Paused = 3,
  Closed = 4,
}
export const PointAccountStatusList = [
  { value: 1, label: 'Draft' },
  { value: 2, label: 'Active' },
  { value: 3, label: 'Paused' },
  { value: 4, label: 'Closed' },
];

export enum PointAccountTargetType {
  RunningClub = 1,
  RunningClubEvent = 2,
  /** App-wide (e.g. sign-up bonus). Has no target entity; the API stores targetId as 0. */
  Platform = 3,
  /** One Energy Hub venue (EnergyHub* rules only). */
  EnergyHub = 4,
}
export const PointAccountTargetTypeList = [
  { value: 1, label: 'Running Club' },
  { value: 2, label: 'Running Club Event' },
  { value: 3, label: 'Platform (app-wide)' },
  { value: 4, label: 'Energy Hub' },
];

export enum PointEarnTrigger {
  ClubEventCheckIn = 1,
  /** Third-party sign-up, or email sign-up once verified. Needs a Platform assignment. */
  UserSignUp = 2,
  /** A runner scans the hub tag (solo, or on an approved Hub Run). Filter with the visit condition. */
  EnergyHubCheckIn = 3,
  /** Staff approve a receipt (solo or club). */
  EnergyHubPurchase = 4,
}
export const PointEarnTriggerList = [
  { value: 1, label: 'Club Event Check-In' },
  { value: 2, label: 'User Sign-Up' },
  { value: 3, label: 'Energy Hub Check-In' },
  { value: 4, label: 'Energy Hub Purchase' },
];

export const isEnergyHubTrigger = (t?: PointEarnTrigger) =>
  t == PointEarnTrigger.EnergyHubCheckIn || t == PointEarnTrigger.EnergyHubPurchase;

/** Visit condition for Energy Hub rules, sent as the NAME, e.g. {"visit":"Club"}. */
export type VisitCondition = 'Any' | 'Club' | 'Solo';
export const VisitConditionList: { value: VisitCondition; label: string }[] = [
  { value: 'Any', label: 'Any visit' },
  { value: 'Club', label: 'On a Hub Run (club)' },
  { value: 'Solo', label: 'Solo (purchase only)' },
];

export enum PointEarnPeriod {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
}
export const PointEarnPeriodList = [
  { value: 1, label: 'Daily' },
  { value: 2, label: 'Weekly' },
  { value: 3, label: 'Monthly' },
];

/**
 * Attendee condition for ClubEventCheckIn rules. The API parses the conditions JSON with a
 * string-only enum converter, so this must be sent as the NAME, e.g. {"attendee":"Newcomer"}.
 */
export type AttendeeCondition = 'Any' | 'Newcomer' | 'Returning';
export const AttendeeConditionList: { value: AttendeeCondition; label: string }[] = [
  { value: 'Any', label: 'Any attendee' },
  { value: 'Newcomer', label: 'Newcomer (first check-in at this club)' },
  { value: 'Returning', label: 'Returning attendee' },
];

export enum PointAccountTransactionReason {
  Fund = 1,
  Grant = 2,
  Adjust = 3,
}
export const PointAccountTransactionReasonList = [
  { value: 1, label: 'Fund' },
  { value: 2, label: 'Grant' },
  { value: 3, label: 'Adjust' },
];

export function pointAccountListFromJson(json: any): PointAccountList {
  return {
    ...json,
    startsAt: utcToJSDate(json.startsAt),
    endsAt: utcToJSDate(json.endsAt),
    createdAt: utcToJSDate(json.createdAt),
  } as PointAccountList;
}

export function pointAccountFromJson(json: any): PointAccount {
  return {
    ...pointAccountListFromJson(json),
    assignments: json.assignments ?? [],
    rules: json.rules ?? [],
  } as PointAccount;
}

export interface PointAccountList {
  id: number;
  name: string;
  pointTypeId: number;
  pointTypeName?: string;
  status: PointAccountStatus;
  startsAt?: Date;
  endsAt?: Date;
  createdAt: Date;
}

export interface PointAccount extends PointAccountList {
  description?: string;
  balance: number;
  totalFunded: number;
  totalGranted: number;
  assignments: PointAccountAssignment[];
  rules: PointEarnRule[];
}

export interface UpsertPointAccount {
  id?: number;
  name: string;
  description?: string;
  pointTypeId?: number;
  status: PointAccountStatus;
  startsAt?: Date;
  endsAt?: Date;
}

export interface PointAccountFundRequest {
  amount?: number;
  note?: string;
  idempotencyKey?: string;
}

export interface PointAccountAdjustRequest {
  amount?: number;
  note?: string;
}

export interface PointAccountAssignment {
  id: number;
  targetType: PointAccountTargetType;
  targetId: number;
}

export interface PointAccountAssignmentRequest {
  targetType?: PointAccountTargetType;
  targetId?: number;
}

export interface PointEarnRule {
  id: number;
  pointAccountId: number;
  name: string;
  trigger: PointEarnTrigger;
  conditions: { attendee?: AttendeeCondition } | any;
  amount: number;
  expiresAfterDays?: number;
  priority: number;
  maxPerUser?: number;
  maxPerUserPerPeriod?: number;
  period?: PointEarnPeriod;
  isActive: boolean;
}

export interface UpsertPointEarnRule {
  name: string;
  trigger: PointEarnTrigger;
  /** ClubEventCheckIn: { attendee }. EnergyHub*: { visit }. UserSignUp takes no conditions: send {}. */
  conditions: { attendee?: AttendeeCondition; visit?: VisitCondition };
  amount?: number;
  expiresAfterDays?: number;
  priority: number;
  maxPerUser?: number;
  maxPerUserPerPeriod?: number;
  period?: PointEarnPeriod;
  isActive: boolean;
}

export const getRuleAttendee = (rule?: PointEarnRule): AttendeeCondition => {
  const raw = rule?.conditions?.attendee ?? rule?.conditions?.Attendee;
  return (AttendeeConditionList.find(a => a.value.toLowerCase() === String(raw ?? '').toLowerCase())?.value) ?? 'Any';
};

export function pointAccountTransactionFromJson(json: any): PointAccountTransaction {
  return { ...json, createdAt: utcToJSDate(json.createdAt) } as PointAccountTransaction;
}

export interface PointAccountTransaction {
  id: number;
  pointAccountId: number;
  amount: number;
  reason: PointAccountTransactionReason;
  userPointTransactionId?: number;
  note?: string;
  createdBy: number;
  createdAt: Date;
}

export function pointEarnRecordFromJson(json: any): PointEarnRecord {
  return { ...json, createdAt: utcToJSDate(json.createdAt) } as PointEarnRecord;
}

export interface PointEarnRecord {
  id: number;
  pointEarnRuleId: number;
  userId: number;
  sourceType: number;
  sourceId: number;
  pointTransactionId: number;
  createdAt: Date;
}

export class PointAccountPaginationFilter extends PaginationFilterRequest {
  pointTypeId?: number;
  status?: PointAccountStatus;
  targetType?: PointAccountTargetType;
  targetId?: number;
}

export const getRuleVisit = (rule?: PointEarnRule): VisitCondition => {
  const raw = rule?.conditions?.visit ?? rule?.conditions?.Visit;
  return (VisitConditionList.find(v => v.value.toLowerCase() === String(raw ?? '').toLowerCase())?.value) ?? 'Any';
};
